import API_URL from "../config/ApiUrl";

const API_BASE_URL = API_URL;

class SeshRoomApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Helper method for making authenticated requests
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    console.log('Sesh Room API request to:', url);
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Use session-based auth like your AuthService
    };

    const requestOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, requestOptions);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Sesh Room API request failed:', error);
      throw error;
    }
  }

  // ============== LEGACY METHODS (keep for backward compatibility) ==============
  
  // Legacy: Get all sesh rooms
  async getSeshRooms() {
    return this.makeRequest('/api/get-sesh-rooms');
  }

  // Legacy: Get specific sesh room
  async getSeshRoom(roomData) {
    return this.makeRequest('/api/get/sesh-room', {
      method: 'POST',
      body: JSON.stringify(roomData),
    });
  }

  // Legacy: Add sesh room
  async addSeshRoom(roomData) {
    return this.makeRequest('/api/add-sesh-room', {
      method: 'POST',
      body: JSON.stringify(roomData),
    });
  }

  // Legacy: Join sesh room
  async joinSeshRoom(joinData) {
    return this.makeRequest('/api/join-sesh-room', {
      method: 'POST',
      body: JSON.stringify(joinData),
    });
  }

  // Legacy: Send message
  async sendMessage(messageData) {
    return this.makeRequest('/api/send-message', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  }

  // ============== NEW ENHANCED METHODS ==============

  // Get enhanced rooms with filtering
  async getRooms(filters = {}) {
    const params = new URLSearchParams();
    
    // Add filters to params
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });
    
    const queryString = params.toString();
    const endpoint = queryString ? `/api/rooms?${queryString}` : '/api/rooms';
    
    return this.makeRequest(endpoint);
  }

  // Get specific room details with participants and messages
  async getRoom(roomId) {
    return this.makeRequest(`/api/rooms/${roomId}`);
  }

  // Create new enhanced room
  async createRoom(roomData) {
    console.log('Creating room with data:', roomData);
    return this.makeRequest('/api/rooms', {
      method: 'POST',
      body: JSON.stringify(roomData),
    });
  }

  // Join a room (enhanced with invite code support)
  async joinRoom(roomId, inviteCode = null) {
    const body = {};
    if (inviteCode) {
      body.invite_code = inviteCode;
    }
    
    return this.makeRequest(`/api/rooms/${roomId}/join`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Leave a room
  async leaveRoom(roomId) {
    return this.makeRequest(`/api/rooms/${roomId}/leave`, {
      method: 'POST',
    });
  }

  // Send message to room (enhanced)
  async sendRoomMessage(roomId, content, messageType = 'text') {
    return this.makeRequest(`/api/rooms/${roomId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ 
        content, 
        message_type: messageType 
      }),
    });
  }

  // Start streaming (host only)
  async startStream(roomId) {
    return this.makeRequest(`/api/rooms/${roomId}/stream/start`, {
      method: 'POST',
    });
  }

  // Stop streaming (host only)
  async stopStream(roomId) {
    return this.makeRequest(`/api/rooms/${roomId}/stream/stop`, {
      method: 'POST',
    });
  }

  // Search rooms
  async searchRooms(query, filters = {}) {
    const searchFilters = { search: query, ...filters };
    return this.getRooms(searchFilters);
  }

  // Get rooms by category
  async getRoomsByCategory(category) {
    return this.getRooms({ category });
  }

  // Get live rooms only
  async getLiveRooms() {
    return this.getRooms({ is_live: true });
  }

  // Get public rooms only
  async getPublicRooms() {
    return this.getRooms({ type: 'public' });
  }

  // ============== UTILITY METHODS ==============

  // Get room participants
  async getRoomParticipants(roomId) {
    return this.makeRequest(`/api/rooms/${roomId}/participants`);
  }

  // Get room messages with pagination
  async getRoomMessages(roomId, page = 1, limit = 50) {
    return this.makeRequest(`/api/rooms/${roomId}/messages?page=${page}&limit=${limit}`);
  }

  // Update room settings (host only)
  async updateRoom(roomId, updateData) {
    return this.makeRequest(`/api/rooms/${roomId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  // Delete room (host only)
  async deleteRoom(roomId) {
    return this.makeRequest(`/api/rooms/${roomId}`, {
      method: 'DELETE',
    });
  }

  // Invite user to private room
  async inviteToRoom(roomId, userId) {
    return this.makeRequest(`/api/rooms/${roomId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  }

  // Get user's rooms (hosting or participating)
  async getUserRooms() {
    return this.makeRequest('/api/rooms/my-rooms');
  }

  // Report room or message
  async reportContent(type, id, reason) {
    return this.makeRequest('/api/reports', {
      method: 'POST',
      body: JSON.stringify({
        content_type: type, // 'room' or 'message'
        content_id: id,
        reason: reason
      }),
    });
  }

  // Get popular strains (for suggestions)
  async getPopularStrains() {
    return this.makeRequest('/api/strains/popular');
  }
}

// Create and export singleton instance
const seshRoomApi = new SeshRoomApiService();

export default seshRoomApi;

// Named export for easier importing
export { seshRoomApi };

// Convenience exports for specific use cases
export const legacySeshRoomApi = {
  getSeshRooms: () => seshRoomApi.getSeshRooms(),
  getSeshRoom: (roomData) => seshRoomApi.getSeshRoom(roomData),
  addSeshRoom: (roomData) => seshRoomApi.addSeshRoom(roomData),
  joinSeshRoom: (joinData) => seshRoomApi.joinSeshRoom(joinData),
  sendMessage: (messageData) => seshRoomApi.sendMessage(messageData),
};

export const enhancedSeshRoomApi = {
  getRooms: (filters) => seshRoomApi.getRooms(filters),
  getRoom: (roomId) => seshRoomApi.getRoom(roomId),
  createRoom: (roomData) => seshRoomApi.createRoom(roomData),
  joinRoom: (roomId, inviteCode) => seshRoomApi.joinRoom(roomId, inviteCode),
  leaveRoom: (roomId) => seshRoomApi.leaveRoom(roomId),
  sendMessage: (roomId, content, messageType) => seshRoomApi.sendRoomMessage(roomId, content, messageType),
  startStream: (roomId) => seshRoomApi.startStream(roomId),
  stopStream: (roomId) => seshRoomApi.stopStream(roomId),
  searchRooms: (query, filters) => seshRoomApi.searchRooms(query, filters),
  getLiveRooms: () => seshRoomApi.getLiveRooms(),
  getPublicRooms: () => seshRoomApi.getPublicRooms(),
  getRoomsByCategory: (category) => seshRoomApi.getRoomsByCategory(category),
};

// Hook for React components
export const useSeshRoomApi = () => {
  return seshRoomApi;
};